<script setup lang="ts">
import { computed } from 'vue';
import BaseIcon from '@/components/base/BaseIcon.vue';
import { isRecord, toLocalizedNullableNumber, toNullableString } from '@/utils/coerce';
import { formatDate, formatNumber } from '@/utils/formatters';
import type { KycCategory } from '@/types/kyc';

interface AgeCompanyRecord {
  openingDate: string | null;
  businessAgeYears: number | null;
}

interface Props {
  category: KycCategory;
}

const props = defineProps<Props>();

const ageDetails = computed<AgeCompanyRecord | null>(() => {
  const raw = props.category.data_json;
  if (!isRecord(raw)) return null;

  return {
    openingDate: toNullableString(raw.opening_date),
    businessAgeYears: toLocalizedNullableNumber(raw.business_age_years),
  };
});

const formattedBusinessAge = computed(() => {
  const years = ageDetails.value?.businessAgeYears;
  if (years === null || years === undefined) return 'Idade não informada';

  const rounded = Number(years.toFixed(1));
  const label = formatNumber(rounded);
  return rounded === 1 ? '1 ano' : `${label} anos`;
});
</script>

<template>
  <div class="mt-3">
    <div
      v-if="ageDetails"
      class="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"
    >
      <div class="grid grid-cols-1 divide-y divide-slate-100 md:grid-cols-[1.2fr_1fr] md:divide-x md:divide-y-0">
        <div class="p-4">
          <div class="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            Idade da empresa
          </div>
          <div class="mt-3 flex items-center gap-3">
            <div class="flex h-12 w-12 items-center justify-center rounded-xl bg-sky-50 text-sky-700">
              <BaseIcon class="text-2xl">business</BaseIcon>
            </div>
            <div>
              <div class="text-2xl font-semibold text-slate-900">{{ formattedBusinessAge }}</div>
              <div class="text-sm text-slate-500">Calculada a partir da data de abertura encontrada</div>
            </div>
          </div>
        </div>

        <div class="p-4">
          <div class="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            Data de abertura
          </div>
          <div class="mt-3 flex items-center gap-3">
            <div class="flex h-12 w-12 items-center justify-center rounded-xl bg-slate-100 text-slate-600">
              <BaseIcon class="text-2xl">event</BaseIcon>
            </div>
            <div>
              <div class="text-lg font-semibold text-slate-900">
                {{ formatDate(ageDetails.openingDate, undefined, 'Data não informada') }}
              </div>
              <div class="text-sm text-slate-500">Primeira data de inscrição disponível no relatório</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="text-sm italic text-slate-500">
      Nenhum dado de idade da empresa encontrado.
    </div>
  </div>
</template>
